import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { MessageSquare, Search, Trash2, Reply, Loader2, XCircle, CheckCircle, ChevronLeft, ChevronRight } from "lucide-react";

const ITEMS_PER_PAGE = 10;

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [deletingMessage, setDeletingMessage] = useState(null);
  const [replyingMessage, setReplyingMessage] = useState(null);
  const [replyText, setReplyText] = useState("");

  const [message, setMessage] = useState({ type: '', text: '' });
  const API_URL = process.env.REACT_APP_API_URL;
  
  // ✅ Verileri backend'den çekme işlemi
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        // Doğru endpoint'i kullanın
        const response = await axios.get(`${API_URL}/messages`);
        setMessages(response.data);
        setError(null);
      } catch (err) {
        console.error("Mesajlar alınamadı:", err);
        setError("Mesajlar yüklenirken bir hata oluştu.");
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, [API_URL]);

  const filteredData = useMemo(() => {
    return messages.filter((item) => {
      const name = item.name ?? "";
      const email = item.email ?? "";
      const messageText = item.message ?? "";

      const matchSearch =
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        messageText.toLowerCase().includes(searchTerm.toLowerCase());
      return matchSearch;
    });
  }, [searchTerm, messages]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const confirmDelete = (msg) => {
    setDeletingMessage(msg);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/messages/${deletingMessage.id}`);
      setMessages((prev) => prev.filter((m) => m.id !== deletingMessage.id));
      setMessage({ type: 'success', text: 'Mesaj başarıyla silindi!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      console.error("Silme hatası:", err);
      setMessage({ type: 'error', text: 'Mesaj silinirken bir hata oluştu.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setDeletingMessage(null);
    }
  };

  const cancelDelete = () => setDeletingMessage(null);

  const openReplyModal = (msg) => {
    setReplyingMessage(msg);
    setReplyText("");
  };

  const saveReply = async () => {
    if (!replyText.trim()) {
      setMessage({ type: 'error', text: 'Lütfen cevap metni girin.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    try {
      // ✅ Cevaplama endpoint'ine POST isteği gönderiliyor
      await axios.post(`${API_URL}/messages/${replyingMessage.id}/reply`, {
        reply_content: replyText, // Backend'de 'reply_content' olarak adlandıracağız
      });
      setMessage({ type: 'success', text: 'Cevap başarıyla gönderildi!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      
      // ✅ Cevaplanan mesajın durumunu güncelleyelim
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === replyingMessage.id ? { ...msg, is_replied: true } : msg
        )
      );
    } catch (err) {
      console.error("Cevap gönderilemedi:", err);
      setMessage({ type: 'error', text: 'Cevap gönderilirken bir hata oluştu.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setReplyingMessage(null);
    }
  };

  const cancelReply = () => setReplyingMessage(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 text-gray-50">
        <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
        <p className="ml-4 text-xl">Mesajlar yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-red-400 p-6 text-center">
        <XCircle className="h-16 w-16 mb-4" />
        <p className="text-2xl font-semibold">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6 bg-gray-900 text-gray-50 min-h-screen font-sans">
      <div className="flex items-center gap-4 mb-8">
        <MessageSquare className="h-12 w-12 text-blue-400" />
        <h1 className="text-4xl font-extrabold text-white">Mesajlar</h1>
      </div>

      {message.type && (
        <div className={`flex items-center gap-3 p-4 mb-6 rounded-xl border ${
          message.type === 'success' ? 'bg-green-900/30 text-green-400 border-green-700' :
          message.type === 'error' ? 'bg-red-900/30 text-red-400 border-red-700' :
          'bg-blue-900/30 text-blue-400 border-blue-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="h-6 w-6 flex-shrink-0" /> : <XCircle className="h-6 w-6 flex-shrink-0" />}
          <span className="font-medium">{message.text}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0 mb-8 p-4 bg-gray-800 rounded-2xl border border-gray-700">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="İsim, e-posta veya mesaj ara..."
            className="w-full pl-10 pr-4 py-3 bg-gray-700 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600 transition-colors"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      <div className="bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-700">
        <table className="min-w-full text-gray-300">
          <thead className="bg-gray-700 border-b border-gray-600">
            <tr className="text-left text-sm font-semibold text-gray-400">
              <th className="px-6 py-3">ID</th>
              <th className="px-6 py-3">İsim</th>
              <th className="px-6 py-3">E-posta</th>
              <th className="px-6 py-3">Mesaj</th>
              <th className="px-6 py-3">Tarih</th>
              <th className="px-6 py-3">Durum</th> {/* ✅ Yeni sütun */}
              <th className="px-6 py-3">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-6 text-gray-500">Kayıt bulunamadı.</td>
              </tr>
            ) : (
              paginatedData.map((msg) => (
                <tr key={msg.id} className="hover:bg-gray-700 transition-colors duration-150">
                  <td className="px-6 py-4">{msg.id}</td>
                  <td className="px-6 py-4 font-semibold text-white">{msg.name}</td>
                  <td className="px-6 py-4">{msg.email}</td>
                  <td className="px-6 py-4 max-w-xs">{msg.message}</td>
                  <td className="px-6 py-4 text-sm">{msg.date}</td>
                  <td className="px-6 py-4">
                    {msg.is_replied ? (
                      <span className="inline-flex items-center rounded-full bg-green-500/20 px-2.5 py-0.5 text-xs font-medium text-green-400">
                        Cevaplandı
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-yellow-500/20 px-2.5 py-0.5 text-xs font-medium text-yellow-400">
                        Beklemede
                      </span>
                    )}
                  </td> {/* ✅ Yeni durum sütunu */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => openReplyModal(msg)}
                        className="inline-flex items-center gap-1 bg-green-600/20 text-green-400 px-3 py-1 rounded-lg hover:bg-green-600/30 transition-colors duration-200 text-sm"
                      >
                        <Reply className="h-4 w-4" /> Cevapla
                      </button>
                      <button
                        onClick={() => confirmDelete(msg)}
                        className="inline-flex items-center gap-1 bg-red-600/20 text-red-400 px-3 py-1 rounded-lg hover:bg-red-600/30 transition-colors duration-200 text-sm"
                      >
                        <Trash2 className="h-4 w-4" /> Sil
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-3 mt-8">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              currentPage === 1 ? "text-gray-600 bg-gray-800 cursor-not-allowed" : "text-white bg-gray-700 hover:bg-gray-600"
            }`}
          >
            <ChevronLeft className="h-5 w-5" /> Önceki
          </button>
          <span className="px-4 py-2 rounded-lg bg-gray-700 text-white font-semibold">{currentPage} / {totalPages}</span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              currentPage === totalPages ? "text-gray-600 bg-gray-800 cursor-not-allowed" : "text-white bg-gray-700 hover:bg-gray-600"
            }`}
          >
            Sonraki <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Silme Onayı Modalı */}
      {deletingMessage && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm z-50 p-4"
          onClick={cancelDelete}
        >
          <div
            className="bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-sm border border-gray-700 text-gray-50 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-4">Silme Onayı</h3>
            <p className="text-gray-300 mb-6">
              **{deletingMessage.name}** isimli kullanıcının mesajını silmek istediğine emin misin? Bu işlem geri alınamaz.
            </p>
            <div className="flex justify-center gap-4">
              <button
                type="button"
                onClick={cancelDelete}
                className="px-6 py-3 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors font-semibold"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cevaplama Modalı */}
      {replyingMessage && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm z-50 p-4"
          onClick={cancelReply}
        >
          <div
            className="bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-lg border border-gray-700 text-gray-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold">Mesaja Cevapla</h3>
              <button
                type="button"
                onClick={cancelReply}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6 p-4 bg-gray-700 rounded-lg border border-gray-600">
              <p className="text-sm font-semibold text-gray-400 mb-1">Gönderen: {replyingMessage.name} ({replyingMessage.email})</p>
              <p className="text-gray-200">{replyingMessage.message}</p>
            </div>

            <textarea
              rows={5}
              className="w-full px-4 py-3 bg-gray-700 text-gray-200 rounded-lg shadow-inner border border-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
              placeholder="Cevap metnini yazınız..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
            />
            
            <div className="flex justify-end gap-4 mt-8">
              <button
                type="button"
                onClick={cancelReply}
                className="px-6 py-3 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors font-semibold"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={saveReply}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
              >
                Gönder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
